import {
  Paper,
  Grid,
  Box,
  Typography,
  Link,
  Stack,
  Divider,
  useTheme,
} from "@mui/material";
import { SocialIcon } from "react-social-icons";
import { format as timeAgoFormat } from "timeago.js";
import { isNil, omit, pick, values, uniq, uniqBy } from "lodash";
import { differenceInMonths, parseISO } from "date-fns";
import dayjs from "dayjs";
import normalizeUrl from "normalize-url";

import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import AutoGraphOutlinedIcon from "@mui/icons-material/AutoGraphOutlined";
import PersonPinOutlinedIcon from "@mui/icons-material/PersonPinOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import LockPersonOutlinedIcon from "@mui/icons-material/LockPersonOutlined";
import NoAdultContentOutlinedIcon from "@mui/icons-material/NoAdultContentOutlined";

import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import TrendingFlatOutlinedIcon from "@mui/icons-material/TrendingFlatOutlined";
import InsertCommentOutlinedIcon from "@mui/icons-material/InsertCommentOutlined";

import { SocialMediaImage } from "./SocialMediaImage";

import { parseDomainFromUrl } from "./utils/parseDomainFromUrl";
import Tooltip from "../../Tooltip";
import PrintOnly, { useIsPrint } from "../../PrintOnly";
import APISocialMediaProfile from "../../../types/APISocialMediaProfile";
import {
  formatNumberForDisplay,
  tzAwareHumanizeTimeString,
} from "../../../utilities/formatting";

export default function SocialMedia({
  socialMediaLinks,
  socialMediaProfiles,
}: {
  socialMediaLinks: string[] | null;
  socialMediaProfiles: APISocialMediaProfile[] | null;
}) {
  let validatedProfiles: APISocialMediaProfile[] = [];
  for (const profile of socialMediaProfiles || []) {
    if (!profile.url) continue;

    // valid url?
    try {
      new URL(profile.url);
    } catch {
      continue;
    }

    validatedProfiles.push({
      ...profile,
      url: normalizeUrl(profile.url),
    });
  }
  validatedProfiles = uniqBy(validatedProfiles, "url");
  const validatedProfileUrls = validatedProfiles.map((p) => p.url);

  let validatedLinks: string[] = [];
  for (const link of socialMediaLinks || []) {
    if (!link) continue;

    // valid url?
    let linkURL: URL;
    try {
      linkURL = new URL(link);
    } catch {
      continue;
    }

    const normalizedLink = normalizeUrl(link);

    if (validatedProfileUrls.includes(normalizedLink)) {
      continue;
    }

    // facebook urls are case-insensitive, so we can be more aggressive about deduping
    if (linkURL.hostname.match(/facebook/i)) {
      if (
        validatedProfileUrls
          .map((url) => url.toLowerCase())
          .includes(normalizedLink.toLowerCase())
      ) {
        continue;
      }
    }

    validatedLinks.push(normalizedLink);
  }
  validatedLinks = uniq(validatedLinks);

  const title = (
    <Typography
      variant="h6"
      component="h3"
      sx={{ fontWeight: "normal", mb: 1 }}
    >
      Social Media
    </Typography>
  );

  if (validatedProfiles.length + validatedLinks.length === 0) {
    return (
      <Paper elevation={2} sx={{ mt: 3, p: 3 }}>
        {title}

        <Typography marginTop={1}>No social media found</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ mt: 0, p: 3 }}>
      {title}

      <Grid container spacing={2} marginTop={-2} columnSpacing={{ md: 6 }}>
        {validatedProfiles.map((profile) => (
          <Grid key={profile.url} item xs={12}>
            <SocialMediaProfileRow profile={profile} />
          </Grid>
        ))}

        {validatedLinks.map((url) => (
          <Grid key={url} item xs={12}>
            <SocialMediaProfileRow profile={{ url }} />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

const SOCIAL_MEDIA_NETWORK_NAMES = {
  "facebook.com": "Facebook",
  "instagram.com": "Instagram",
  "twitter.com": "X",
  "x.com": "X",
  "youtube.com": "YouTube",
  "yelp.com": "Yelp",
  "tripadvisor.com": "TripAdvisor",
  "foursquare.com": "Foursquare",
  "pinterest.com": "Pinterest",
  "linkedin.com": "LinkedIn",
  "tiktok.com": "TikTok",
  "snapchat.com": "Snapchat",
  "tumblr.com": "Tumblr",
  "reddit.com": "Reddit",
  "twitch.tv": "Twitch",
  "soundcloud.com": "SoundCloud",
  "vimeo.com": "Vimeo",
  "flickr.com": "Flickr",
  "medium.com": "Medium",
  "quora.com": "Quora",
  "google.com": "Google",
  "crunchbase.com": "Crunchbase",
};

function SocialMediaProfileRow({
  profile,
}: {
  profile: APISocialMediaProfile;
}) {
  const theme = useTheme();
  const isPrint = useIsPrint();
  const nakedDomain = parseDomainFromUrl(
    profile.url
  ) as keyof typeof SOCIAL_MEDIA_NETWORK_NAMES;

  const properName = SOCIAL_MEDIA_NETWORK_NAMES[nakedDomain];

  // show the simple view if everything except the profile url is empty
  const showSimpleView = values(omit(profile, "url")).every((v) => isNil(v));

  let isActive: boolean;
  try {
    // did the account post in the last 3 months?
    isActive = profile.last_posted_content_at
      ? differenceInMonths(
          new Date(),
          parseISO(profile.last_posted_content_at)
        ) < 3
      : false;
  } catch {
    isActive = false;
  }

  let formattedLastPostDate: string | null;
  try {
    formattedLastPostDate = profile.last_posted_content_at
      ? timeAgoFormat(parseISO(profile.last_posted_content_at))
      : null;
  } catch {
    formattedLastPostDate = null;
  }

  // any of these fields being filled will trigger the
  // post analysis section to show
  const postAnalysisAvailable = values(
    pick(profile, [
      "post_quantity",
      "average_engagement",
      "follow_ratio",
      "last_posted_content_at",
      "most_recent_post_url",
      "most_recent_post_text",
      "most_recent_post_image",
    ])
  ).some((v) => {
    if (typeof v === "number") return v !== 0;
    if (typeof v === "string") return v !== "";
    return !isNil(v);
  });

  return (
    <Box display="flex" alignItems={showSimpleView ? "center" : "flex-start"}>
      <Box paddingRight={2}>
        <Tooltip
          key={profile.url}
          title={profile.url}
          placement="bottom"
          disablePrintOnlyMagic
        >
          <span>
            <SocialIcon
              target="_blank"
              url={profile.url}
              network={profile.url.match(/twitter.com\//i) ? "x" : undefined}
              style={{
                width: 45,
                height: 45,
              }}
            />
          </span>
        </Tooltip>
      </Box>

      <Box width="100%">
        <Typography variant="body1" fontWeight="bold" component="div">
          <Tooltip
            key={profile.url}
            title={profile.url}
            placement="right"
            enterDelay={1500}
            enterNextDelay={1500}
            disablePrintOnlyMagic
          >
            <Link
              color="inherit"
              underline="hover"
              target="_blank"
              href={profile.url}
            >
              {properName || nakedDomain}
            </Link>
          </Tooltip>
        </Typography>

        <PrintOnly>
          <Typography variant="body2" component="div">
            {profile.url}
          </Typography>
        </PrintOnly>

        {!showSimpleView && (
          <>
            <Stack
              direction="row"
              columnGap="1em"
              variant="body1"
              fontSize={15}
              flexWrap="wrap"
              color={theme.palette.grey[700]}
              mt={0.25}
              mb={0.25}
            >
              {profile.follower_count ? (
                <Box display="inline-flex" flexShrink={0}>
                  <PersonPinOutlinedIcon
                    fontSize="small"
                    style={{ marginRight: "0.125em" }}
                  />
                  {formatNumberForDisplay(profile.follower_count, true)}{" "}
                  followers
                </Box>
              ) : profile.like_count ? (
                <Box display="inline-flex" flexShrink={0}>
                  <PersonPinOutlinedIcon
                    fontSize="small"
                    style={{ marginRight: "0.125em" }}
                  />
                  {formatNumberForDisplay(profile.like_count, true)} followers
                </Box>
              ) : null}

              {profile.is_verified && (
                <Box display="inline-flex" flexShrink={0}>
                  <VerifiedUserOutlinedIcon
                    fontSize="small"
                    style={{ marginRight: "0.125em" }}
                  />{" "}
                  Verified
                </Box>
              )}

              {profile.is_business && (
                <Box display="inline-flex" flexShrink={0}>
                  <StorefrontOutlinedIcon
                    fontSize="small"
                    style={{ marginRight: "0.125em" }}
                  />{" "}
                  Business Profile
                </Box>
              )}

              {profile.is_age_restricted && (
                <Box display="inline-flex" flexShrink={0}>
                  <NoAdultContentOutlinedIcon
                    fontSize="small"
                    style={{ marginRight: "0.125em" }}
                  />{" "}
                  Age-restricted
                </Box>
              )}

              {profile.is_private && (
                <Box display="inline-flex" flexShrink={0}>
                  <LockPersonOutlinedIcon
                    fontSize="small"
                    style={{ marginRight: "0.125em" }}
                  />{" "}
                  Private
                </Box>
              )}

              {profile.last_posted_content_at &&
                isActive &&
                !profile.activity_level && (
                  <Box display="inline-flex" flexShrink={0}>
                    <AutoGraphOutlinedIcon
                      fontSize="small"
                      style={{ marginRight: "0.125em" }}
                    />{" "}
                    Active
                  </Box>
                )}

              {profile.page_created_at && (
                <Box display="inline-flex" flexShrink={0}>
                  <CakeOutlinedIcon
                    fontSize="small"
                    style={{ marginRight: "0.125em" }}
                  />{" "}
                  Created {dayjs(profile.page_created_at).format("MMMM YYYY")}
                </Box>
              )}

              {profile.activity_level &&
                (profile.activity_level === "High" ? (
                  <Box display="inline-flex" flexShrink={0}>
                    <ArrowUpwardOutlinedIcon
                      fontSize="small"
                      style={{ marginRight: "0.125em" }}
                    />{" "}
                    High Activity
                  </Box>
                ) : profile.activity_level === "Medium" ? (
                  <Box display="inline-flex" flexShrink={0}>
                    <TrendingFlatOutlinedIcon
                      fontSize="small"
                      style={{ marginRight: "0.125em" }}
                    />{" "}
                    Medium Activity
                  </Box>
                ) : profile.activity_level === "Low" ? (
                  <Box display="inline-flex" flexShrink={0}>
                    <ArrowDownwardOutlinedIcon
                      fontSize="small"
                      style={{ marginRight: "0.125em" }}
                    />{" "}
                    Low Activity
                  </Box>
                ) : null)}

              {profile.follow_ratio &&
                (profile.follow_ratio === "High" ? (
                  <Box display="inline-flex" flexShrink={0}>
                    <ArrowUpwardOutlinedIcon
                      fontSize="small"
                      style={{ marginRight: "0.125em" }}
                    />{" "}
                    High Follow Ratio
                  </Box>
                ) : profile.follow_ratio === "Medium" ? (
                  <Box display="inline-flex" flexShrink={0}>
                    <TrendingFlatOutlinedIcon
                      fontSize="small"
                      style={{ marginRight: "0.125em" }}
                    />{" "}
                    Medium Follow Ratio
                  </Box>
                ) : profile.follow_ratio === "Low" ? (
                  <Box display="inline-flex" flexShrink={0}>
                    <ArrowDownwardOutlinedIcon
                      fontSize="small"
                      style={{ marginRight: "0.125em" }}
                    />{" "}
                    Low Follow Ratio
                  </Box>
                ) : null)}
            </Stack>

            {postAnalysisAvailable && (
              <>
                <Divider />
                <Stack>
                  <details
                    style={{ marginTop: "0.5em" }}
                    {...(isPrint ? { open: true } : {})}
                  >
                    <summary style={{ cursor: "pointer" }}>
                      Post Analysis
                      {formattedLastPostDate && (
                        <span
                          style={{
                            paddingLeft: "0.5em",
                            fontSize: "85%",
                            color: theme.palette.grey[500],
                          }}
                        >
                          Last post {formattedLastPostDate}
                        </span>
                      )}
                    </summary>

                    <Stack
                      direction="row"
                      columnGap="1em"
                      variant="body1"
                      fontSize={15}
                      flexWrap="wrap"
                      color={theme.palette.grey[700]}
                      mt={1}
                      mb={1}
                      ml={1.5}
                    >
                      {!!profile.post_quantity && (
                        <Box display="inline-flex" flexShrink={0}>
                          <InsertCommentOutlinedIcon
                            fontSize="small"
                            style={{ marginRight: "0.125em" }}
                          />
                          {formatNumberForDisplay(profile.post_quantity)} Posts
                        </Box>
                      )}
                      {profile.average_engagement &&
                        (profile.average_engagement === "High" ? (
                          <Box display="inline-flex" flexShrink={0}>
                            <ArrowUpwardOutlinedIcon
                              fontSize="small"
                              style={{ marginRight: "0.125em" }}
                            />{" "}
                            High Engagement
                          </Box>
                        ) : profile.average_engagement === "Medium" ? (
                          <Box display="inline-flex" flexShrink={0}>
                            <TrendingFlatOutlinedIcon
                              fontSize="small"
                              style={{ marginRight: "0.125em" }}
                            />{" "}
                            Medium Engagement
                          </Box>
                        ) : profile.average_engagement === "Low" ? (
                          <Box display="inline-flex" flexShrink={0}>
                            <ArrowDownwardOutlinedIcon
                              fontSize="small"
                              style={{ marginRight: "0.125em" }}
                            />{" "}
                            Low Engagement
                          </Box>
                        ) : null)}
                    </Stack>

                    {profile.last_posted_content_at &&
                      profile.most_recent_post_url && (
                        <Link
                          target="_blank"
                          href={profile.most_recent_post_url}
                          underline="none"
                          color="inherit"
                        >
                          <Box
                            border={`1px solid ${theme.palette.grey[300]}`}
                            padding={2}
                            ml={1.5}
                            borderRadius="0.75em"
                            maxWidth="500px"
                          >
                            <Typography>
                              <strong>
                                Latest Post &bull;{" "}
                                {tzAwareHumanizeTimeString(
                                  profile.last_posted_content_at
                                )}
                              </strong>
                            </Typography>
                            {profile.most_recent_post_text && (
                              <Typography mt={1}>
                                {profile.most_recent_post_text}
                              </Typography>
                            )}
                            {profile.most_recent_post_image && (
                              <SocialMediaImage
                                src={profile.most_recent_post_image}
                              />
                            )}
                          </Box>
                        </Link>
                      )}
                  </details>
                </Stack>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
